import React, { useState } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Save, Banknote } from 'lucide-react';
import { toast } from 'sonner';

export default function CreditCard({ api, credit, solicitareId, banci = [], onChanged }) {
  const isNew = !credit?.id;
  const [c, setC] = useState({ moneda: 'RON', ...credit });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setC((p) => ({ ...p, [k]: v }));

  const expira = c.dataExpirareDobandaFixa;
  let luni = null;
  if (expira) {
    const ed = new Date(expira);
    const now = new Date();
    luni = (ed.getFullYear() - now.getFullYear()) * 12 + (ed.getMonth() - now.getMonth());
  }
  const soon = luni != null && luni <= 12;

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        banca: c.banca || null,
        valoare: c.valoare ? Number(c.valoare) : null,
        dobanda: c.dobanda ? Number(c.dobanda) : null,
        perioadaLuni: c.perioadaLuni ? Number(c.perioadaLuni) : null,
        perioadaFixaLuni: c.perioadaFixaLuni ? Number(c.perioadaFixaLuni) : null,
        moneda: c.moneda || 'RON',
        dataDisbursarii: c.dataDisbursarii ? String(c.dataDisbursarii).slice(0, 10) : null,
        notite: c.notite || null,
      };
      if (isNew) {
        if (!c.banca) {
          toast.error('Alege banca la care e creditul');
          setSaving(false);
          return;
        }
        await api.post(`/solicitari/${solicitareId}/credit`, payload);
        toast.success('Credit înregistrat');
      } else {
        await api.put(`/credite/${c.id}`, payload);
        toast.success('Credit actualizat');
      }
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Eroare la salvare credit');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border rounded-lg p-3 space-y-3" data-testid={`crm-credit-${c.id || 'nou'}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <Banknote className="h-4 w-4 text-blue-600" />
        <select
          className="border rounded-md h-8 px-2 text-sm font-medium"
          value={c.banca || ''}
          onChange={(e) => set('banca', e.target.value)}
          data-testid={`crm-credit-banca-${c.id || 'nou'}`}
        >
          <option value="">Alege banca</option>
          {[...new Set([...(banci || []), c.banca].filter(Boolean))].map((b) => (
            <option key={b} value={b}>{b}</option>
          ))}
        </select>
        <span className="text-xs text-gray-500">{c.tipCredit || '—'}</span>
        {expira && (
          <Badge className={soon ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-700'}>
            Dobândă fixă expiră {expira}{luni != null ? ` (~${Math.max(luni, 0)} luni)` : ''}
          </Badge>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <Label className="text-[11px] text-gray-500">Valoare</Label>
          <Input type="number" value={c.valoare || ''} onChange={(e) => set('valoare', e.target.value)} />
        </div>
        <div>
          <Label className="text-[11px] text-gray-500">Dobândă %</Label>
          <Input type="number" step="0.01" value={c.dobanda || ''} onChange={(e) => set('dobanda', e.target.value)} />
        </div>
        <div>
          <Label className="text-[11px] text-gray-500">Perioadă (luni)</Label>
          <Input type="number" value={c.perioadaLuni || ''} onChange={(e) => set('perioadaLuni', e.target.value)} />
        </div>
        <div>
          <Label className="text-[11px] text-gray-500">Dob. fixă (luni)</Label>
          <Input type="number" value={c.perioadaFixaLuni || ''}
            onChange={(e) => set('perioadaFixaLuni', e.target.value)} placeholder="36"
            data-testid={`crm-credit-fix-${c.id || 'nou'}`} />
        </div>
        <div>
          <Label className="text-[11px] text-gray-500">Data disbursării</Label>
          <Input type="date" value={(c.dataDisbursarii || '').slice(0, 10)}
            onChange={(e) => set('dataDisbursarii', e.target.value)} />
        </div>
        <div>
          <Label className="text-[11px] text-gray-500">Monedă</Label>
          <select className="w-full border rounded-md h-9 px-2 text-sm" value={c.moneda || 'RON'}
            onChange={(e) => set('moneda', e.target.value)}>
            <option value="RON">RON</option>
            <option value="EUR">EUR</option>
          </select>
        </div>
      </div>
      <Button size="sm" onClick={save} disabled={saving} data-testid={`crm-credit-save-${c.id || 'nou'}`}>
        <Save className="h-4 w-4 mr-1" />
        {saving ? 'Se salvează…' : (isNew ? 'Înregistrează creditul' : 'Salvează credit')}
      </Button>
    </div>
  );
}
